import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  User, Check, AlertCircle, Loader2, Send, Search, CheckCircle, XCircle
} from 'lucide-react';
import api, { tablesAPI, agircAPI } from '../services/api';

const FormulairePublic = () => {
  const { lienUnique } = useParams();
  const navigate = useNavigate();
  
  const [lienInfo, setLienInfo] = useState(null);
  const [caisses, setCaisses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [reference, setReference] = useState('');
  
  // État pour la recherche AGIRC-ARRCO
  const [rechercheEnCours, setRechercheEnCours] = useState(false);
  const [rechercheResultat, setRechercheResultat] = useState(null); // 'found', 'not_found', 'error', null
  const [rechercheMessage, setRechercheMessage] = useState('');
  const [nssValide, setNssValide] = useState(null); // true, false, null
  const [nssErreur, setNssErreur] = useState('');
  
  // Formulaire simplifié: uniquement Identité + Contact + NSS
  const [formData, setFormData] = useState({
    // Identité
    nom: '',
    prenom: '',
    date_naissance: '',
    nom_usage: '',
    numero_securite_sociale: '',
    cle_securite_sociale: '',
    
    // Contact
    adresse: '',
    code_postal: '',
    ville: '',
    telephone: '',
    telephone_fixe: '',
    email: '',
    pays: 'France',
    
    // Caisse (sera remplie automatiquement ou manuellement)
    caisse: ''
  });

  useEffect(() => {
    loadLienInfo();
  }, [lienUnique]);

  const loadLienInfo = async () => {
    try {
      setLoading(true);
      const [lienRes, caissesRes] = await Promise.all([
        api.get(`/liens-partenaires/public/${lienUnique}`),
        tablesAPI.getCaisses()
      ]);
      setLienInfo(lienRes.data);
      setCaisses(caissesRes.data || []);
    } catch (err) {
      setError('Ce lien est invalide ou a été désactivé.');
    } finally {
      setLoading(false);
    }
  };

  // Validation du NSS (algorithme modulo 97)
  const validerNSS = useCallback((nss, cle) => {
    // Nettoyer le NSS (enlever espaces et points)
    const nssClean = nss.replace(/[\s.]/g, '');
    const cleClean = cle.replace(/\s/g, '');
    
    // Vérifier la longueur
    if (nssClean.length !== 13) {
      return { valide: false, erreur: 'Le numéro doit contenir 13 chiffres' };
    }
    
    if (cleClean.length !== 2) {
      return { valide: false, erreur: 'La clé doit contenir 2 chiffres' };
    }
    
    // Vérifier que ce sont des chiffres (sauf A/B pour la Corse)
    const nssForCalc = nssClean.replace(/[Aa]/g, '0').replace(/[Bb]/g, '0');
    if (!/^\d{13}$/.test(nssForCalc)) {
      return { valide: false, erreur: 'Format du numéro invalide' };
    }
    
    if (!/^\d{2}$/.test(cleClean)) {
      return { valide: false, erreur: 'La clé doit être composée de 2 chiffres' };
    }
    
    // Calcul de la clé (modulo 97)
    const nssInt = parseInt(nssForCalc, 10);
    const cleCalculee = 97 - (nssInt % 97);
    const cleEntree = parseInt(cleClean, 10);
    
    if (cleEntree !== cleCalculee) {
      return { valide: false, erreur: 'La clé de contrôle est incorrecte' };
    }
    
    return { valide: true, erreur: '' };
  }, []);

  // Recherche automatique de la caisse
  const rechercherCaisse = useCallback(async (nom, prenom, nss, cle, tentative = 1) => {
    const MAX_TENTATIVES = 3;
    
    setRechercheEnCours(true);
    setRechercheResultat(null);
    setRechercheMessage('Recherche de votre caisse en cours...');
    
    try {
      const response = await agircAPI.rechercheCaisse({
        nom: nom,
        prenom: prenom,
        nss: nss.replace(/[\s.]/g, ''),
        cle_nss: cle.replace(/\s/g, '')
      });
      
      const data = response.data;
      
      if (data.success && data.caisse) {
        // Caisse trouvée !
        setFormData(prev => ({ ...prev, caisse: data.caisse }));
        setRechercheResultat('found');
        setRechercheMessage(`Caisse trouvée : ${data.caisse}`);
      } else if (data.error && data.error.includes('non affilié')) {
        // Pas affilié
        setRechercheResultat('not_found');
        setRechercheMessage('Aucune caisse complémentaire trouvée');
      } else {
        // Autre erreur (service indisponible, etc.)
        throw new Error(data.error || 'Erreur de recherche');
      }
    } catch (err) {
      console.error(`Tentative ${tentative}/${MAX_TENTATIVES} échouée:`, err);
      
      if (tentative < MAX_TENTATIVES) {
        // Réessayer après un délai
        setRechercheMessage(`Nouvelle tentative (${tentative + 1}/${MAX_TENTATIVES})...`);
        setTimeout(() => {
          rechercherCaisse(nom, prenom, nss, cle, tentative + 1);
        }, 1500);
        return;
      } else {
        // Après 3 tentatives, on abandonne silencieusement
        setRechercheResultat('error');
        setRechercheMessage('');
      }
    } finally {
      if (tentative >= MAX_TENTATIVES || rechercheResultat) {
        setRechercheEnCours(false);
      }
    }
  }, []);

  // Effet pour déclencher la recherche automatique
  useEffect(() => {
    const nss = formData.numero_securite_sociale;
    const cle = formData.cle_securite_sociale;
    const nom = formData.nom;
    const prenom = formData.prenom;
    
    // Nettoyer les états si les champs sont modifiés
    if (!nss || !cle) {
      setNssValide(null);
      setNssErreur('');
      setRechercheResultat(null);
      setRechercheMessage('');
      return;
    }
    
    // Vérifier si NSS et clé sont complets
    const nssClean = nss.replace(/[\s.]/g, '');
    const cleClean = cle.replace(/\s/g, '');
    
    if (nssClean.length === 13 && cleClean.length === 2) {
      // Valider le NSS
      const validation = validerNSS(nss, cle);
      setNssValide(validation.valide);
      setNssErreur(validation.erreur);
      
      // Si valide et nom/prénom renseignés, lancer la recherche
      if (validation.valide && nom && prenom && !rechercheEnCours) {
        // Reset la caisse avant la recherche
        setFormData(prev => ({ ...prev, caisse: '' }));
        rechercherCaisse(nom, prenom, nss, cle);
      }
    } else {
      setNssValide(null);
      setNssErreur('');
    }
  }, [formData.numero_securite_sociale, formData.cle_securite_sociale, formData.nom, formData.prenom, validerNSS, rechercherCaisse]);

  const handleSubmit = async () => {
    if (!formData.nom || !formData.prenom) {
      setError('Le nom et le prénom sont obligatoires');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const res = await api.post(`/liens-partenaires/public/${lienUnique}/soumettre`, formData);
      setSuccess(true);
      setReference(res.data.reference);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Chargement du formulaire...</p>
        </div>
      </div>
    );
  }

  if (error && !lienInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Lien invalide</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Demande envoyée !</h1>
          <p className="text-gray-600 mb-4">
            Votre demande a été enregistrée avec succès.
          </p>
          <div className="bg-green-50 rounded-xl p-4 mb-6">
            <p className="text-sm text-green-700">Votre numéro de référence :</p>
            <p className="text-2xl font-bold text-green-800 font-mono">{reference}</p>
          </div>
          <p className="text-sm text-gray-500">
            Conservez ce numéro pour le suivi de votre demande.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Nouvelle demande</h1>
          <p className="text-gray-600">
            Formulaire de demande via <span className="font-semibold text-indigo-600">{lienInfo?.nom}</span>
          </p>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <p className="text-sm text-blue-800">
            <strong>Étape 1 :</strong> Renseignez vos informations d'identité et de contact. 
            Un conseiller vous recontactera pour compléter votre dossier.
          </p>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-xl flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}

          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <User className="w-6 h-6 text-indigo-600" />
              Votre identité
            </h2>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                <input
                  type="text"
                  data-testid="input-nom"
                  value={formData.nom}
                  onChange={(e) => updateField('nom', e.target.value.toUpperCase())}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                <input
                  type="text"
                  data-testid="input-prenom"
                  value={formData.prenom}
                  onChange={(e) => updateField('prenom', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de naissance</label>
                <input
                  type="date"
                  data-testid="input-date-naissance"
                  value={formData.date_naissance}
                  onChange={(e) => updateField('date_naissance', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom d'usage</label>
                <input
                  type="text"
                  data-testid="input-nom-usage"
                  value={formData.nom_usage}
                  onChange={(e) => updateField('nom_usage', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            {/* NSS avec validation et recherche automatique */}
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">N° Sécurité sociale *</label>
                <div className="relative">
                  <input
                    type="text"
                    data-testid="input-nss"
                    value={formData.numero_securite_sociale}
                    onChange={(e) => updateField('numero_securite_sociale', e.target.value.replace(/[^0-9AaBb\s]/g, ''))}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-indigo-500 ${
                      nssValide === false ? 'border-red-300 bg-red-50' : 
                      nssValide === true ? 'border-green-300 bg-green-50' : 
                      'border-gray-300'
                    }`}
                    placeholder="1 XX XX XX XXX XXX"
                    maxLength={15}
                  />
                  {nssValide === true && (
                    <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                  )}
                  {nssValide === false && (
                    <XCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Clé *</label>
                <div className="relative">
                  <input
                    type="text"
                    data-testid="input-cle-nss"
                    value={formData.cle_securite_sociale}
                    onChange={(e) => updateField('cle_securite_sociale', e.target.value.replace(/[^0-9]/g, ''))}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-indigo-500 ${
                      nssValide === false ? 'border-red-300 bg-red-50' : 
                      nssValide === true ? 'border-green-300 bg-green-50' : 
                      'border-gray-300'
                    }`}
                    maxLength={2}
                    placeholder="XX"
                  />
                </div>
              </div>
            </div>
            
            {/* Message d'erreur NSS */}
            {nssValide === false && nssErreur && (
              <div className="flex items-center gap-2 text-sm text-red-600 -mt-4">
                <AlertCircle className="w-4 h-4" />
                {nssErreur}
              </div>
            )}

            {/* Caisse Complémentaire - recherche automatique uniquement */}
            <div className="bg-gray-50 rounded-xl p-4 -mx-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Caisse de retraite complémentaire
              </label>
              
              {/* Indicateur de recherche */}
              {rechercheEnCours && (
                <div className="flex items-center gap-3 mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                  <span className="text-sm text-blue-700">{rechercheMessage}</span>
                </div>
              )}
              
              {/* Résultat : Caisse trouvée */}
              {!rechercheEnCours && rechercheResultat === 'found' && (
                <div className="flex items-center gap-3 mb-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-green-700">{rechercheMessage}</span>
                </div>
              )}
              
              {/* Résultat : Pas de caisse ou erreur -> Continuer le formulaire */}
              {!rechercheEnCours && (rechercheResultat === 'not_found' || rechercheResultat === 'error') && (
                <div className="flex items-center gap-3 mb-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  <span className="text-sm text-amber-700">
                    {rechercheResultat === 'not_found' 
                      ? "Aucune caisse complémentaire trouvée. Continuez le formulaire."
                      : "Recherche impossible pour le moment. Continuez le formulaire."
                    }
                  </span>
                </div>
              )}
              
              {/* Champ caisse - toujours grisé (lecture seule) */}
              <input
                type="text"
                data-testid="input-caisse"
                value={formData.caisse || (rechercheResultat === 'found' ? '' : '— Sera déterminée par votre conseiller —')}
                disabled
                className={`w-full px-4 py-3 border rounded-xl cursor-not-allowed ${
                  rechercheResultat === 'found' 
                    ? 'border-green-300 bg-green-50 text-green-800' 
                    : 'border-gray-300 bg-gray-100 text-gray-500'
                }`}
                readOnly
              />
              
              <p className="text-xs text-gray-500 mt-2">
                {!nssValide && "Renseignez votre nom, prénom et numéro de sécurité sociale pour une recherche automatique."}
                {nssValide === true && !rechercheResultat && "La recherche s'effectue automatiquement..."}
                {rechercheResultat === 'found' && "Caisse identifiée automatiquement."}
                {(rechercheResultat === 'not_found' || rechercheResultat === 'error') && "Votre conseiller identifiera votre caisse."}
              </p>
            </div>

            <hr className="my-6 border-gray-200" />

            <h3 className="text-lg font-medium text-gray-900">Coordonnées</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
              <input
                type="text"
                data-testid="input-adresse"
                value={formData.adresse}
                onChange={(e) => updateField('adresse', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code postal</label>
                <input
                  type="text"
                  data-testid="input-code-postal"
                  value={formData.code_postal}
                  onChange={(e) => updateField('code_postal', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                <input
                  type="text"
                  data-testid="input-ville"
                  value={formData.ville}
                  onChange={(e) => updateField('ville', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pays</label>
                <input
                  type="text"
                  data-testid="input-pays"
                  value={formData.pays}
                  onChange={(e) => updateField('pays', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone portable</label>
                <input
                  type="tel"
                  data-testid="input-telephone"
                  value={formData.telephone}
                  onChange={(e) => updateField('telephone', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  data-testid="input-email"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-8 pt-6 border-t">
            <button
              type="button"
              data-testid="submit-form-btn"
              onClick={handleSubmit}
              disabled={submitting || !formData.nom || !formData.prenom || rechercheEnCours}
              className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-lg"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Envoi en cours...
                </>
              ) : rechercheEnCours ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Recherche en cours...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Envoyer ma demande
                </>
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Vos données sont protégées et traitées de manière confidentielle.
        </p>
      </div>
    </div>
  );
};

export default FormulairePublic;
